import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Send, CheckCircle2, FileText, Shield, Star, X, Inbox, LayoutGrid, Download, Pencil, Trash2, Rocket, Hash } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead, PremiumBanner, StatCard } from "@/components/kit";
import TicketPanelModal from "@/components/TicketPanelModal";
import { AiTicketBadge, AiConversationPanel, AiSummaryCard } from "@/components/AiTicketPanel";

const BTN_HEX = { blurple: "#5865F2", green: "#248046", red: "#da373c", grey: "#4e5058" };

export default function Tickets() {
  const { guildId, guild } = useGuild();
  const locked = false;
  const [tab, setTab] = useState("panels");
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [stats, setStats] = useState(null);
  const [panels, setPanels] = useState([]);
  const [filter, setFilter] = useState("");
  const [transcript, setTranscript] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadList = () => api.get(`/guilds/${guildId}/tickets${filter === "ai" ? "?source=ai_agent" : filter ? `?status=${filter}` : ""}`).then((r) => setTickets(r.data));
  const loadStats = () => api.get(`/guilds/${guildId}/tickets/stats`).then((r) => setStats(r.data));
  const loadPanels = () => api.get(`/guilds/${guildId}/tickets/panels`).then((r) => setPanels(r.data));
  useEffect(() => { loadList(); loadStats(); loadPanels(); }, [guildId, filter]);

  const openDetail = (id) => api.get(`/guilds/${guildId}/tickets/${id}`).then((r) => setActive(r.data));
  // Live sync: Discord messages/claims/closes appear without refreshing.
  useEffect(() => {
    if (!active?.id || active.status !== "open") return undefined;
    const t = setInterval(() => openDetail(active.id), 5000);
    return () => clearInterval(t);
  }, [active?.id, active?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePanel = async (payload) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/guilds/${guildId}/tickets/panels/${editing.id}`, payload);
      else await api.post(`/guilds/${guildId}/tickets/panels`, payload);
      toast.success(editing ? "Panel updated" : "Panel created");
      setModalOpen(false); setEditing(null); loadPanels();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  const deletePanel = async (id) => {
    try { await api.delete(`/guilds/${guildId}/tickets/panels/${id}`); toast.success("Panel deleted"); loadPanels(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const postPanel = async (id) => {
    try { const { data } = await api.post(`/guilds/${guildId}/tickets/panels/${id}/post`); toast.success(`Panel posted to #${data.channel}`); loadPanels(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };

  const simulate = async () => {
    try { await api.post(`/guilds/${guildId}/tickets/simulate`); toast.success("New ticket opened"); loadList(); loadStats(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/guilds/${guildId}/tickets/${active.id}/messages`, { content: reply });
      setReply(""); openDetail(active.id);
      if (!data.relayed) toast.message("Saved on the dashboard only — this ticket has no linked Discord channel.");
    }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const claim = async () => {
    try { await api.post(`/guilds/${guildId}/tickets/${active.id}/claim`); toast.success("Ticket claimed"); openDetail(active.id); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const close = async () => {
    try {
      const { data } = await api.post(`/guilds/${guildId}/tickets/${active.id}/close`, { reason: "Resolved" });
      toast.success(active.channel_id ? "Ticket closed · the Discord channel closes in a few seconds and the member is asked for a rating" : "Ticket closed · transcript saved");
      openDetail(active.id); loadList(); loadStats();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const viewTranscript = async () => {
    const { data } = await api.get(`/guilds/${guildId}/tickets/${active.id}/transcript`);
    setTranscript(data.transcript);
  };
  const downloadTranscript = () => {
    const blob = new Blob([transcript], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ticket-${active.number}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHead title="Ticket Support" desc="Guildbase-style multi-panel ticket system with form questions, transcripts & stats.">
        <div className="flex gap-2">
          <button data-testid="ticket-simulate" onClick={simulate} className="border border-zinc-700 text-zinc-300 rounded-md px-4 py-2 hover:bg-zinc-900 flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> Simulate ticket</button>
          <button data-testid="create-panel-btn" onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Plus className="w-4 h-4" /> Create Panel</button>
        </div>
      </PageHead>
      <PremiumBanner guildId={guildId} tier="Pro" locked={locked} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Ticket} label="Total" value={stats.total} />
          <StatCard icon={Inbox} label="Open" value={stats.open} accent="#f59e0b" />
          <StatCard icon={CheckCircle2} label="Closed" value={stats.closed} accent="#10b981" />
          <StatCard icon={Star} label="Avg Rating" value={`${stats.avg_rating}★`} accent="#facc15" />
        </div>
      )}

      <div className="inline-flex bg-zinc-900 p-1 rounded-lg mb-6">
        <button data-testid="ticket-tab-panels" onClick={() => setTab("panels")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === "panels" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><LayoutGrid className="w-4 h-4 inline mr-1" /> Panels</button>
        <button data-testid="ticket-tab-inbox" onClick={() => setTab("inbox")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === "inbox" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><Inbox className="w-4 h-4 inline mr-1" /> Inbox</button>
      </div>

      {tab === "panels" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {panels.map((p) => (
            <div key={p.id} data-testid="panel-card" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-volt/15 flex items-center justify-center shrink-0"><Ticket className="w-5 h-5 text-volt" /></div>
                  <div className="min-w-0"><div className="font-head font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-1"><Hash className="w-3 h-3" />{p.panel_channel || "no channel set"}</div></div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${p.active ? "bg-volt/15 text-volt" : "bg-zinc-800 text-zinc-500"}`}>{p.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                <span>{(p.questions || []).length} question{(p.questions || []).length !== 1 && "s"}</span>
                <span>Max {p.max_open_per_user}/user</span>
                <span className="capitalize">{p.interaction_type}</span>
                {p.posted_message_id && <span className="text-volt">● Posted</span>}
              </div>
              <div className="mt-3"><button className="text-white text-xs font-medium px-3 py-1.5 rounded" style={{ background: BTN_HEX[p.button_color] || BTN_HEX.blurple }}>{p.button_label}</button></div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-800">
                <button data-testid="panel-post" onClick={() => postPanel(p.id)} className="flex-1 text-xs bg-volt/10 text-volt border border-volt/30 rounded-md px-3 py-1.5 hover:bg-volt/20 flex items-center justify-center gap-1"><Rocket className="w-3.5 h-3.5" /> Post</button>
                <button data-testid="panel-edit" onClick={() => { setEditing(p); setModalOpen(true); }} className="text-xs bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5 flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid="panel-delete" onClick={() => deletePanel(p.id)} className="text-xs bg-zinc-800 hover:bg-red-500/20 hover:text-red-400 rounded-md px-2.5 py-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <button data-testid="create-panel-card" onClick={() => { setEditing(null); setModalOpen(true); }} className="border border-dashed border-zinc-700 rounded-xl p-5 flex flex-col items-center justify-center gap-2 text-zinc-500 hover:border-volt hover:text-volt min-h-[180px]">
            <Plus className="w-7 h-7" /> <span className="text-sm font-medium">Create Ticket Panel</span>
          </button>
        </div>
      )}

      {tab === "inbox" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex flex-wrap gap-1 mb-3">
              {["", "open", "closed", "ai"].map((ff) => (
                <button key={ff} data-testid={`ticket-filter-${ff || "all"}`} onClick={() => setFilter(ff)} className={`text-xs px-3 py-1 rounded-full ${filter === ff ? "bg-volt text-volt-ink" : "bg-zinc-800 text-zinc-400"}`}>{ff === "" ? "All" : ff === "ai" ? "Opened by AI" : ff}</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {tickets.map((t) => (
                <button key={t.id} data-testid="ticket-row" onClick={() => openDetail(t.id)} className={`w-full text-left bg-black/30 border rounded-lg p-3 hover:border-zinc-600 transition-colors ${active?.id === t.id ? "border-volt/50" : "border-zinc-800"}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-500">#{String(t.number).padStart(4, "0")}</span>
                    <span className={`w-2 h-2 rounded-full ${t.status === "open" ? "bg-[#f59e0b]" : "bg-zinc-600"}`} />
                    <span className="text-sm font-medium truncate flex-1">{t.subject}</span>
                    {t.source === "ai_agent" && <AiTicketBadge testId={`ticket-row-ai-badge-${t.number}`} />}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{t.opener} · {t.category}</div>
                </button>
              ))}
              {tickets.length === 0 && <p className="text-zinc-600 text-sm">No tickets yet. Use "Simulate ticket" to create one.</p>}
            </div>
          </div>

          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl flex flex-col min-h-[560px]">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-zinc-600 text-sm">Select a ticket to view the conversation.</div>
            ) : (
              <>
                <div className="p-4 border-b border-zinc-800 flex items-center gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="font-head font-semibold flex items-center gap-2">#{String(active.number).padStart(4, "0")} · {active.subject}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${active.status === "open" ? "bg-[#f59e0b]/15 text-[#f59e0b]" : "bg-zinc-800 text-zinc-500"}`}>{active.status}</span>
                      {active.source === "ai_agent" && <AiTicketBadge testId="ticket-detail-ai-badge" />}</div>
                    <div className="text-xs text-zinc-500">{active.opener} · {active.category} {active.assigned_staff && `· claimed by ${active.assigned_staff}`} {active.channel_id && <span data-testid="ticket-discord-synced" className="text-volt">· 🔗 synced with Discord #{active.channel_name}</span>}</div>
                  </div>
                  {active.status === "open" && <>
                    <button data-testid="ticket-claim" onClick={claim} className="text-xs bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5 flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Claim</button>
                    <button data-testid="ticket-close" onClick={close} className="text-xs bg-red-500/15 text-red-400 border border-red-500/30 rounded-md px-3 py-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Close</button>
                  </>}
                  {active.status === "closed" && <button data-testid="ticket-transcript" onClick={viewTranscript} className="text-xs bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Transcript</button>}
                </div>
                {active.ai_conversation && <AiSummaryCard key={`s-${active.id}`} guildId={guildId} ticket={active} onUpdated={() => openDetail(active.id)} />}
                {active.ai_conversation && <AiConversationPanel key={active.id} convo={active.ai_conversation} />}
                {(active.answers || []).length > 0 && (
                  <div className="px-4 py-3 border-b border-zinc-800 bg-black/20 space-y-2">
                    {active.answers.map((a, i) => (
                      <div key={i} data-testid="ticket-answer" className="text-sm"><span className="text-zinc-500 text-xs">{a.question}</span><div className="text-zinc-200">{a.answer}</div></div>
                    ))}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {active.messages?.filter((m) => !m.ai_summary).map((m, i) => (
                    <div key={i} data-testid="ticket-message" className={`flex gap-3 ${m.staff ? "flex-row-reverse" : ""}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${m.staff ? "bg-volt text-volt-ink" : "bg-zinc-700"}`}>{m.author[0]}</div>
                      <div className={`max-w-[75%] ${m.staff ? "text-right" : ""}`}>
                        <div className="text-xs text-zinc-500">{m.author} {m.staff && "· Staff"} {m.source && <span data-testid="ticket-message-source" className="text-zinc-600">· via {m.source}</span>}</div>
                        <div className={`text-sm rounded-lg px-3 py-2 mt-1 inline-block ${m.staff ? "bg-volt/10 text-zinc-100" : "bg-black/40 text-zinc-200"}`}>{m.content}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {active.status === "open" && (
                  <div className="p-3 border-t border-zinc-800 flex gap-2">
                    <input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply as staff…" className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
                    <button data-testid="ticket-reply-send" onClick={sendReply} className="bg-volt text-volt-ink rounded-md px-4 flex items-center"><Send className="w-4 h-4" /></button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <TicketPanelModal open={modalOpen} initial={editing} saving={saving} guildId={guildId} onClose={() => { setModalOpen(false); setEditing(null); }} onSave={savePanel} />

      {transcript && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setTranscript(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="font-head font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-volt" /> Transcript</div>
              <div className="flex gap-2">
                <button data-testid="transcript-download" onClick={downloadTranscript} className="text-xs bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5 flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download</button>
                <button data-testid="transcript-close" onClick={() => setTranscript(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <pre data-testid="transcript-body" className="flex-1 overflow-auto p-4 text-xs font-mono text-zinc-300 whitespace-pre-wrap">{transcript}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
